import type { WyrmDef } from "../rare/wyrmApi";
import { btnHtml, motionOk } from "./kit";
import { resourceIconUrl } from "./portraits";
import { paintWyrm, wyrmFaceHtml } from "./wyrmUi";
import type { WyrmChoiceModel } from "./wyrmModel";
import { WyrmTopUp } from "./wyrmTopUp";
import type { WyrmStore } from "./wyrmTopUp";

/**
 * The choice's answers: the paid retry (true once paid, or why not), letting it go, and the gem store for a top-up
 * (null when this build has none: the sheet then says where gems are won).
 */
export interface WyrmChoiceActions {
  retry(): true | string;
  release(): void;
  readonly store: WyrmStore | null;
}

/**
 * The Starfall Wyrm flew off and the battle waits (M40: `sim.wyrmChoiceOpen` pauses the action): a sheet over a dimmed
 * map with its portrait, how hurt it would come back, CHALLENGE AGAIN for the gems (greyed when the Commander is short,
 * with TOP UP beside it: the one in-battle gem store, ui/wyrmTopUp.ts, which redraws the sheet with the new balance on
 * a purchase) and "Let it go". One answer per sheet: both buttons lock as soon as one is pressed.
 */
export class WyrmChoiceSheet {
  private el: HTMLDivElement | null = null;
  private scrim: HTMLDivElement | null = null;
  private readonly topUp: WyrmTopUp;

  constructor(private readonly parent: HTMLElement) { this.topUp = new WyrmTopUp(parent); }

  get shown(): boolean { return this.el !== null; }
  /** The top-up is open over it. */
  get toppingUp(): boolean { return this.topUp.shown; }

  show(def: WyrmDef, model: WyrmChoiceModel, act: WyrmChoiceActions, refresh: () => WyrmChoiceModel): void {
    this.hide();
    const scrim = document.createElement("div");
    scrim.className = "wyrm-choice-scrim";
    const el = document.createElement("div");
    el.className = "sheet panel wyrm-choice";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", model.title);
    paintWyrm(el, def);
    this.parent.append(scrim, el);
    this.el = el; this.scrim = scrim;
    this.render(model);
    el.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest("button");
      if (!b || b.disabled || el.classList.contains("answered")) return;
      if (b.classList.contains("wc-retry")) {
        el.classList.add("answered");
        const r = act.retry();
        if (r !== true) { el.classList.remove("answered"); this.render(refresh()); }
      } else if (b.classList.contains("wc-release")) { el.classList.add("answered"); act.release(); }
      else if (b.classList.contains("wc-topup") && act.store) {
        const before = refresh();
        this.topUp.open(def, act.store, before.shortfall, el, () => {
          const m = refresh();
          this.render(m);
          if (m.affordable && motionOk()) this.el?.querySelector<HTMLElement>(".wc-retry")?.animate([{ transform: "scale(1.12)" }, { transform: "none" }], { duration: 420, easing: "cubic-bezier(.2,1.6,.4,1)" });
        });
      }
    });
    if (motionOk() && typeof el.animate === "function") {
      el.animate([{ transform: "translateY(100%)" }, { transform: "none" }], { duration: 360, easing: "cubic-bezier(.2,1.1,.4,1)" });
      scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300 });
    }
  }

  private render(m: WyrmChoiceModel): void {
    const el = this.el;
    if (!el) return;
    const gem = `<img class="wc-gem" decoding="sync" src="${resourceIconUrl("gems")}" alt="gems">`;
    el.innerHTML = `<div class="wc-head"><div class="wc-face">${wyrmFaceHtml()}</div><div class="wc-t"><b class="disp">${m.title}</b><small>${m.lead}</small></div></div>`
      + `<div class="wc-hp"><div class="wc-track"><i style="transform:scaleX(${m.hpFrac.toFixed(3)})"></i></div><span>${m.hpText}</span></div>`
      + btnHtml(m.retryLabel, "gold", { className: `wc-retry${m.affordable ? "" : " short"}`, disabled: !m.affordable })
      + `<small class="wc-bal${m.affordable ? "" : " short"}">${m.balanceText}</small>`
      + btnHtml(m.releaseLabel, "dark", { className: "wc-release" })
      // the quiet top-up: a sentence-case link under the two answers, never a third button (docs/VOICE.md)
      + (m.topUp ? `<button class="wc-topup">${m.topUp}</button>` : "");
    const retry = el.querySelector<HTMLElement>(".wc-retry .lbl");
    retry?.insertAdjacentHTML("afterend", `<span class="wc-cost">${gem}${m.cost}</span>`);
  }

  hide(): void { this.topUp.hide(); this.el?.remove(); this.scrim?.remove(); this.el = null; this.scrim = null; }
}
